import type { CSSProperties } from 'react';
import { fontSizeOf, fontWeightOf } from '@/config/appStyles';

/**
 * Перенос `lib/src/utils/size_util.dart`.
 *
 * Во Flutter число строк считал `TextPainter.computeLineMetrics()` после
 * `layout(maxWidth: …)`. В браузере ближайший эквивалент — замер ширины
 * текста через 2D-контекст `<canvas>`: ставим тот же шрифт и жадно переносим
 * по пробелам, как это делает движок вёрстки.
 *
 * Результат используется только для выбора отступов (`messageLineCount == 1`)
 * и для включения скролла (`descriptionLength >= 3`), поэтому расхождение
 * в доли пикселя роли не играет.
 */

/** Шрифт приложения — тот же, что в `src/styles/global.css`. */
const MEASURE_FONT_FAMILY = "'SF Pro Display'";

/** Средняя ширина глифа в долях кегля — для оценки без canvas (SSR/jsdom). */
const FALLBACK_CHAR_WIDTH_RATIO = 0.5;

/** У абзаца всегда есть хотя бы одна строка — как у пустого `TextPainter`. */
const MIN_LINE_COUNT = 1;

let measureContext: CanvasRenderingContext2D | null = null;
let measureContextResolved = false;

/**
 * Контекст создаётся один раз и переиспользуется: `createElement('canvas')`
 * на каждый замер заметно дороже самого `measureText`.
 *
 * В SSR и в jsdom без пакета `canvas` контекста нет — тогда `null`, и замер
 * уходит в грубую оценку вместо исключения.
 */
function getMeasureContext(): CanvasRenderingContext2D | null {
  if (measureContextResolved) return measureContext;
  measureContextResolved = true;

  if (typeof document === 'undefined') return null;

  try {
    measureContext = document.createElement('canvas').getContext('2d');
  } catch (error) {
    console.warn(
      'SizeUtil: 2D-контекст canvas недоступен, число строк считается по длине текста',
      error,
    );
    measureContext = null;
  }
  return measureContext;
}

/** Жадный перенос одного абзаца по пробелам. */
function measureParagraphLines(
  context: CanvasRenderingContext2D,
  paragraph: string,
  maxWidth: number,
): number {
  const words = paragraph.split(/\s+/).filter((word) => word.length > 0);
  if (words.length === 0) return MIN_LINE_COUNT;

  let lines = MIN_LINE_COUNT;
  let current = words[0];

  for (let index = 1; index < words.length; index += 1) {
    const candidate = `${current} ${words[index]}`;
    if (context.measureText(candidate).width <= maxWidth) {
      current = candidate;
      continue;
    }
    lines += 1;
    current = words[index];
  }

  return lines;
}

/** Оценка без замера: считаем, сколько символов влезает в строку. */
function estimateLineCount(
  paragraphs: readonly string[],
  style: CSSProperties,
  maxWidth: number,
): number {
  const charWidth = fontSizeOf(style) * FALLBACK_CHAR_WIDTH_RATIO;
  const charsPerLine = Math.max(Math.floor(maxWidth / charWidth), 1);
  return paragraphs.reduce(
    (total, paragraph) =>
      total + Math.max(Math.ceil(paragraph.length / charsPerLine), MIN_LINE_COUNT),
    0,
  );
}

/** `SizeUtil.calculateLineCount(text, style, maxWidth)`. */
export function calculateLineCount(
  text: string,
  style: CSSProperties,
  maxWidth: number,
): number {
  // `split('\n')` на пустой строке даёт `['']` — то есть одну строку,
  // как и `TextPainter` с пустым текстом.
  const paragraphs = text.split('\n');

  // Ширины ещё нет (первый кадр `LayoutBuilder`) — переносить не по чему.
  if (!Number.isFinite(maxWidth) || maxWidth <= 0) {
    return Math.max(paragraphs.length, MIN_LINE_COUNT);
  }

  const context = getMeasureContext();
  if (context === null) return estimateLineCount(paragraphs, style, maxWidth);

  context.font = `${fontWeightOf(style)} ${fontSizeOf(style)}px ${MEASURE_FONT_FAMILY}`;

  return paragraphs.reduce(
    (total, paragraph) => total + measureParagraphLines(context, paragraph, maxWidth),
    0,
  );
}

/** Зеркало `abstract final class SizeUtil` — чтобы вызовы читались как в Dart. */
export const SizeUtil = { calculateLineCount } as const;
